import React, { useEffect, useState, useCallback } from "react";
import { AlertTriangle, Flag, X, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { StepPill, PickPill, PUBLIC_STEPS, ScanBar, EmptyState, Card } from "./fxui";
import { QrLabelButton } from "./QrLabel";

export default function FxOrders({ role = "admin" }) {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [onlyExc, setOnlyExc] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "") params.status = status;
      if (onlyExc) params.exceptions = true;
      if (q) params.q = q;
      const { data } = await api.get("/fx/orders", { params });
      setOrders(data.orders || []);
    } catch { setOrders([]); } finally { setLoading(false); }
  }, [status, onlyExc, q]);

  useEffect(() => { load(); }, [load]);

  const openScan = async (code) => {
    try {
      const { data } = await api.get("/fx/scan", { params: { code } });
      if (data.order_number) setDetail(data.order_number);
    } catch { /* ignore */ }
  };

  return (
    <div data-testid="fx-orders">
      <ScanBar onSubmit={openScan} placeholder="Scan order / package / container code…" testid="fx-orders-scan" />
      <div className="flex flex-wrap gap-3 mb-5 items-center">
        <input data-testid="fx-orders-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search order #, name, phone…"
          className="flex-1 min-w-[220px] bg-white border border-black/10 rounded-full px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#306D29] focus:outline-none" />
        <select data-testid="fx-orders-status" value={status} onChange={(e) => setStatus(e.target.value)}
          className="bg-white border border-black/10 rounded-full px-4 py-2.5 text-sm">
          <option value="">All stages</option>
          {PUBLIC_STEPS.map((s, i) => <option key={i} value={i}>{s}</option>)}
        </select>
        <button data-testid="fx-orders-exc" onClick={() => setOnlyExc((v) => !v)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold border ${onlyExc ? "bg-[#B23B3B] text-white border-[#B23B3B]" : "bg-white text-[#B23B3B] border-[#B23B3B]/30"}`}>
          <AlertTriangle size={15} /> Exceptions
        </button>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#306D29]" /></div>
        : orders.length === 0 ? <EmptyState>No orders match your filters.</EmptyState>
        : (
        <div className="space-y-2" data-testid="fx-orders-list">
          {orders.map((o) => (
            <button key={o.order_number} data-testid={`fx-order-row-${o.order_number}`} onClick={() => setDetail(o.order_number)}
              className="w-full text-left bg-white border border-black/5 rounded-2xl px-5 py-4 hover:border-[#306D29]/40 flex flex-wrap items-center gap-3"
              style={{ transition: "border-color 0.15s ease" }}>
              <div className="flex-1 min-w-[160px]">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[#0D530E]">{o.order_number}</span>
                  {o.priority && <Flag size={13} className="text-[#D9933B] fill-[#D9933B]" />}
                  {o.has_exceptions && <AlertTriangle size={13} className="text-[#B23B3B]" />}
                </div>
                <p className="text-xs text-[#5C5C5C]">{o.customer_name} · {o.delivery_city || "—"}</p>
              </div>
              <span className="text-xs text-[#5C5C5C]">{o.items_resolved}/{o.items_total} items</span>
              <StepPill step={o.public_step} />
            </button>
          ))}
        </div>
      )}
      {detail && <OrderDetail orderNumber={detail} role={role} onClose={() => { setDetail(null); load(); }} />}
    </div>
  );
}

function OrderDetail({ orderNumber, role, onClose }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get(`/fx/orders/${orderNumber}`);
    setData(data);
  }, [orderNumber]);
  useEffect(() => { load(); }, [load]);

  const togglePriority = async () => {
    await api.post(`/fx/orders/${orderNumber}/priority`, { priority: !data.priority });
    load();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div className="bg-[#FBF5DD] w-full max-w-xl h-full overflow-y-auto p-7" onClick={(e) => e.stopPropagation()} data-testid="fx-order-detail">
        <div className="flex justify-between items-start mb-5">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C] font-semibold">Order</p>
            <h3 className="font-display text-3xl text-[#0D530E]">{orderNumber}</h3>
          </div>
          <button onClick={onClose} data-testid="fx-detail-close"><X size={22} /></button>
        </div>
        {!data ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#306D29]" /></div> : (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-3">
              <StepPill step={data.public_step} />
              <span className="text-sm text-[#5C5C5C]">{data.internal_status}</span>
              <QrLabelButton code={orderNumber} title={data.customer.name} subtitle={data.customer.city} testid="fx-order-label" label="Order label" />
              {(role === "admin" || role === "support") && (
                <button onClick={togglePriority} data-testid="fx-detail-priority"
                  className={`ml-auto flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${data.priority ? "bg-[#D9933B] text-white" : "bg-white border border-black/10 text-[#5C5C5C]"}`}>
                  <Flag size={13} /> {data.priority ? "Priority" : "Flag priority"}
                </button>
              )}
            </div>
            <Card>
              <p className="font-semibold mb-1">{data.customer.name}</p>
              <p className="text-sm text-[#5C5C5C]">{data.customer.phone} · {data.customer.city}</p>
              <p className="text-sm text-[#5C5C5C]">{data.customer.address}</p>
            </Card>
            {(role === "admin" || role === "support") && (data.buy_cost > 0 || data.total_gbp) && (
              <Card>
                <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C] font-semibold mb-2">Cost vs price</p>
                <div className="flex justify-between text-sm"><span>You spent buying</span><b>£{(data.buy_cost || 0).toFixed(2)}</b></div>
                <div className="flex justify-between text-sm"><span>Customer paid</span><b>£{Number(data.total_gbp || 0).toFixed(2)}</b></div>
                <div className="flex justify-between text-sm mt-1 pt-1 border-t border-black/5">
                  <span>Margin</span>
                  <b className={(Number(data.total_gbp || 0) - (data.buy_cost || 0)) >= 0 ? "text-[#306D29]" : "text-[#B23B3B]"}>£{(Number(data.total_gbp || 0) - (data.buy_cost || 0)).toFixed(2)}</b>
                </div>
              </Card>
            )}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C] font-semibold mb-2">Items ({data.items.length})</p>
              <div className="space-y-2">
                {data.items.map((it) => (
                  <div key={it.key} className="bg-white border border-black/5 rounded-xl px-4 py-3 flex items-center gap-3">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{it.name}</p>
                      <p className="text-xs text-[#5C5C5C]">Qty {it.quantity} · {it.source_store || "—"}</p>
                      {it.exception_note && <p className="text-xs text-[#B23B3B] mt-0.5">{it.exception_reason}: {it.exception_note}</p>}
                    </div>
                    <PickPill status={it.pick_status} />
                  </div>
                ))}
              </div>
            </div>
            {data.packages.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C] font-semibold mb-2">Packages</p>
                {data.packages.map((p) => (
                  <div key={p.package_number} className="bg-white border border-black/5 rounded-xl px-4 py-2.5 text-sm flex justify-between mb-2">
                    <span>{p.package_number} · {p.package_type} {p.weight_kg ? `· ${p.weight_kg}kg` : ""}</span>
                    <span className={p.qc_status === "passed" ? "text-[#306D29] font-semibold" : "text-[#D9933B]"}>{p.qc_status}</span>
                  </div>
                ))}
              </div>
            )}
            {data.container && (
              <Card>
                <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C] font-semibold mb-1">Container</p>
                <p className="font-semibold">{data.container.container_number} · {data.container.manifest_status}</p>
              </Card>
            )}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C] font-semibold mb-2">History</p>
              <div className="space-y-2">
                {data.events.map((e) => (
                  <div key={e.id} className="text-sm flex gap-3">
                    <span className="text-xs text-[#5C5C5C] w-32 shrink-0">{new Date(e.at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}</span>
                    <span>{e.internal_note} <span className="text-[#5C5C5C]">· {e.by}</span></span>
                  </div>
                ))}
                {data.events.length === 0 && <p className="text-sm text-[#5C5C5C]">No events yet.</p>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
